import React, { useEffect, useRef, useState } from 'react';
import { gcmPhotoUrl, isYelpPhoto } from './helpers';
import './MerchantPhoto.scss';

const RETRY_DELAY_MS = 1500;
const GCM_LOGO = `${process.env.PUBLIC_URL}/img/gcm/gift-card-market-logo.png`;

// Yelp photo for a GCM merchant card, loaded from Yelp's CDN.
// - Lazy: off-screen photos (e.g. later carousel slides) aren't requested up front.
//   Pass `eager` for the cards visible on load so they don't wait to be scrolled into view.
// - One retry, for a photo that fails to load the first time.
// Falls back to the page's placeholder, carrying the Gift Card Market logo, when there's
// no Yelp photo or both attempts fail.
const MerchantPhoto = ({ url, alt, placeholderClassName, eager = false }) => {
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    const retryTimer = useRef(null);

    useEffect(() => () => clearTimeout(retryTimer.current), []);

    if (!url || !isYelpPhoto(url) || failed) {
        return (
            <div className={`gcm-photo-fallback ${placeholderClassName}`}>
                <img className="gcm-photo-fallback__logo" src={GCM_LOGO} alt="" />
            </div>
        );
    }

    const handleError = () => {
        if (attempt === 0) {
            retryTimer.current = setTimeout(() => setAttempt(1), RETRY_DELAY_MS);
        } else {
            setFailed(true);
        }
    };

    // the retry gets a distinct URL so the browser doesn't reuse the failed response
    const photo = gcmPhotoUrl(url);
    const src = attempt ? `${photo}${photo.includes('?') ? '&' : '?'}retry=1` : photo;

    return (
        <img
            key={attempt}
            src={src}
            alt={alt}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            referrerPolicy="no-referrer"
            onError={handleError}
        />
    );
};

export default MerchantPhoto;
